/**
 * The text-only preview of a chat message — what a reply quote, the staged
 * reply chip and a scrubber marker's snippet show for it: its text; a frame
 * sent without text, as `sent a frame at 0:08` (the backend's wording);
 * otherwise its files, by name. A timestamp-only moment has neither frame nor
 * text, so its files speak for it.
 */
import { isMediaAnchor, type MessageAnchor, type MessageAttachment } from '@discovr/types';

import { formatTimecode } from './media/timecode';

/** The frame-only note's label inside its own bubble. */
export const FRAME_ONLY_LABEL = 'Frame sent without a note';
/** Leads a frame-only preview, the moment's timecode following — the backend's `Message.Preview()` wording. */
const FRAME_PREVIEW_PREFIX = 'sent a frame at ';

const EMPTY_PREVIEW = '';

/** What a preview reads off a message. */
export interface PreviewedMessage {
  body: string;
  anchor?: MessageAnchor;
  attachments?: MessageAttachment[];
  /** An optimistic bubble's own frame, shown before its upload gives the anchor a file id. */
  local_frame_url?: string;
}

/** `brief.pdf + 2 more`. */
function filesPreviewLabel(firstName: string, moreCount: number): string {
  return `${firstName} + ${moreCount} more`;
}

/** A message with only files previews as its first file's name, plus how many more. */
function attachmentsPreview(attachments: MessageAttachment[] | undefined): string {
  const [first, ...rest] = attachments ?? [];
  if (!first) return EMPTY_PREVIEW;
  if (rest.length === 0) return first.name;
  return filesPreviewLabel(first.name, rest.length);
}

/** The message's moment carries a captured frame — a timestamp alone doesn't. */
function carriesFrame({ anchor, local_frame_url }: PreviewedMessage): boolean {
  if (local_frame_url) return true;
  return isMediaAnchor(anchor) && !!anchor.frame;
}

/** `sent a frame at 0:08`; a frame with no moment (never sent that way) keeps the bubble's label. */
function framePreview({ anchor }: PreviewedMessage): string {
  if (!isMediaAnchor(anchor)) return FRAME_ONLY_LABEL;
  return `${FRAME_PREVIEW_PREFIX}${formatTimecode(anchor.t_ms)}`;
}

export function messagePreviewText(message: PreviewedMessage): string {
  if (message.body.trim()) return message.body;
  if (carriesFrame(message)) return framePreview(message);
  return attachmentsPreview(message.attachments);
}
